<template>
  <div :class="[...containerClasses, $attrs.class]" :style="cssVars" :id="id">
    <div class="n-section-column__badge" v-if="badgeText && badgeText.length">
      {{ badgeText }}
    </div>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { useNuxtApp } from '#imports';
const BASE_CLASS = 'n-section-column';

interface Props {
  columnSizes?: number[];
  maxWidth?: string;
  minHeight?: string;
  background?: {
    color?: string;
    mobileImage?: string;
    mobileImageSize?: string;
    desktopImage?: string;
    desktopImageSize?: string;
  };
  padding?: {
    result?: {
      mobile?: string;
      tablet?: string;
      desktop?: string;
    };
  };
  margin?: {
    result?: {
      mobile?: string;
      tablet?: string;
      desktop?: string;
    };
  };
  badge?: {
    text?: string;
    background?: string;
    width?: number;
    position?: string;
    offset?: string;
  };
  cssFlags?: string[];
  borderRadius?: string;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  columnSizes: () => [12, 12, 12],
  maxWidth: '100%'
});

defineOptions({
  inheritAttrs: false
});

const { $grid } = useNuxtApp();
const cssVars = computed(() => {
  const vars = {
    '--column-size--sm': props.columnSizes[0],
    '--column-size--md': props.columnSizes[1],
    '--column-size--lg': props.columnSizes[2],
    '--max-width': props.maxWidth,
    '--min-height': props.minHeight,
    '--background': (props.background?.color ?? 'transparent') || 'transparent',
    '--padding-sm': props.padding?.result?.mobile === '' ? '0' : props.padding?.result?.mobile,
    '--padding-md': props.padding?.result?.tablet === '' ? '0' : props.padding?.result?.tablet,
    '--padding-lg': props.padding?.result?.desktop === '' ? '0' : props.padding?.result?.desktop,
    '--margin-sm': props.margin?.result?.mobile === '' ? '0' : props.margin?.result?.mobile,
    '--margin-md': props.margin?.result?.tablet === '' ? '0' : props.margin?.result?.tablet,
    '--margin-lg': props.margin?.result?.desktop === '' ? '0' : props.margin?.result?.desktop,
    '--border-radius': props.borderRadius ?? '0px',
    '--badge-bg-color': props.badge?.background ?? 'transparent',
    '--badge-width': props.badge?.width ? `${props.badge.width}px` : '0px'
  };

  if (props.badge?.position?.split(' ')?.length > 1) {
    let offset = Number(props.badge?.offset || 0);

    if (!$grid.md) {
      offset /= 2;
    }

    const position =
      props.badge?.offset?.length > 0
        ? props.badge.position.replace(/0/g, `${offset}px`).split(' ')
        : props.badge.position.split(' ');

    const [top, right, bottom, left] = position;
    vars['--badge-position-top'] = top;
    vars['--badge-position-right'] = right;
    vars['--badge-position-bottom'] = bottom;
    vars['--badge-position-left'] = left;
  }

  if (String(props.background?.mobileImage ?? '').length) {
    vars['--background-image-mobile'] = `url(${props.background.mobileImage})`;
    vars['--background-image-mobile-size'] = props.background.mobileImageSize || 'cover';
  }

  if (String(props.background?.desktopImage ?? '').length) {
    vars['--background-image-desktop'] = `url(${props.background.desktopImage})`;
    vars['--background-image-desktop-size'] = props.background.desktopImageSize || 'cover';
  }

  return vars;
});

const containerClasses = computed(() => {
  const classes = [BASE_CLASS, ...(props.cssFlags?.map((f) => `${BASE_CLASS}--${f}`) ?? [])];
  if (String(props.background?.mobileImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-mobile`);
  }
  if (String(props.background?.desktopImage ?? '').length) {
    classes.push(`${BASE_CLASS}--background-image-desktop`);
  }
  return classes;
});

const badgeText = computed(() => props.badge?.text ?? '');
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.n-section-column {
  position: relative;
  min-height: var(--min-height);
  max-width: var(--max-width);
  background-color: var(--background);
  grid-column: span var(--column-size--sm);
  // padding: var(--padding-sm);
  margin: var(--margin-sm);
  border-radius: var(--border-radius);

  @include bp(md) {
    grid-column: span var(--column-size--md);
    // padding: var(--padding-md);
    margin: var(--margin-md);
  }
  @include bp(lg) {
    grid-column: span var(--column-size--lg);
    // padding: var(--padding-lg);
    margin: var(--margin-lg);
  }

  &__badge {
    position: absolute;
    background: var(--badge-bg-color);
    left: var(--badge-position-left);
    right: var(--badge-position-right);
    top: var(--badge-position-top);
    bottom: var(--badge-position-bottom);
    border-radius: 50%;
    width: var(--badge-width);
    height: var(--badge-width);
    padding: 0 20px;
    z-index: 2;
    color: #fff;
    display: flex;
    align-items: center;
    text-align: center;
    font-weight: bold;
  }

  &--stretch-height {
    height: max-content;
  }

  &--align-center {
    margin: auto;
  }

  &--text-center {
    text-align: center;
  }

  &--background-image-mobile {
    background-image: var(--background-image-mobile);
    background-size: var(--background-image-mobile-size);
    background-repeat: no-repeat;
    background-position: center;
  }

  &--background-image-desktop {
    @include bp(md) {
      background-image: var(--background-image-desktop);
      background-size: var(--background-image-desktop-size);
      background-repeat: no-repeat;
      background-position: center;
    }
  }
}
</style>
